import { useState } from 'react';
import type { LongTermAssignment, Class } from '@homework-app/shared';

interface LongTermSectionProps {
  assignments: LongTermAssignment[];
  classes: Class[];
  onAdd: (assignment: Omit<LongTermAssignment, 'id' | 'completed' | 'created_at' | 'updated_at'>) => void;
  onUpdate: (id: string, updates: Partial<LongTermAssignment>) => void;
  onDelete: (id: string) => void;
}

function LongTermSection({ assignments, classes, onAdd, onUpdate, onDelete }: LongTermSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'assignment' | 'test' | 'project' | 'essay'>('assignment');
  const [subject, setSubject] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');

  const resetForm = () => {
    setTitle('');
    setType('assignment');
    setSubject('');
    setDueDate('');
    setDescription('');
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !dueDate) return;

    if (editingId) {
      onUpdate(editingId, {
        title: title.trim(),
        type,
        subject: subject || null,
        due_date: dueDate,
        description: description || null
      });
    } else {
      onAdd({
        title: title.trim(),
        type,
        subject: subject || null,
        due_date: dueDate,
        description: description || null
      });
    }
    resetForm();
  };

  const startEdit = (assignment: LongTermAssignment) => {
    setTitle(assignment.title);
    setType(assignment.type);
    setSubject(assignment.subject || '');
    setDueDate(assignment.due_date);
    setDescription(assignment.description || '');
    setEditingId(assignment.id);
    setShowAddForm(true);
  };

  const handleDelete = (assignment: LongTermAssignment) => {
    if (window.confirm(`Are you sure you want to delete "${assignment.title}"?`)) {
      onDelete(assignment.id);
    }
  };

  const getDaysUntil = (dueDate: string): number => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDueDateLabel = (dueDate: string): string => {
    const days = getDaysUntil(dueDate);
    if (days < 0) return 'Overdue';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days <= 7) return `${days} days`;
    return new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDueDateClass = (dueDate: string): string => {
    const days = getDaysUntil(dueDate);
    if (days < 0) return 'overdue';
    if (days <= 1) return 'urgent';
    if (days <= 3) return 'soon';
    return '';
  };

  // Separate and sort incomplete and completed assignments by due date (soonest first)
  const incompleteAssignments = assignments
    .filter(a => !a.completed)
    .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());

  const completedAssignments = assignments
    .filter(a => a.completed)
    .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());

  const incompleteCount = incompleteAssignments.length;
  const enabledClasses = classes.filter(c => c.enabled);

  return (
    <div className="long-term-section">
      <div className="long-term-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="long-term-title">
          <span className="long-term-icon">{isExpanded ? '▼' : '▶'}</span>
          <h2>Upcoming</h2>
          {incompleteCount > 0 && (
            <span className="long-term-badge">{incompleteCount}</span>
          )}
        </div>
        <button
          className="long-term-add-btn"
          onClick={(e) => {
            e.stopPropagation();
            setShowAddForm(true);
          }}
        >
          + Add
        </button>
      </div>

      {isExpanded && (
        <div className="long-term-content">
          {showAddForm && (
            <form className="long-term-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="form-input"
                  autoFocus
                />
              </div>
              <div className="form-row form-row--split">
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as 'assignment' | 'test' | 'project' | 'essay')}
                  className="form-select"
                >
                  <option value="assignment">Assignment</option>
                  <option value="test">Test</option>
                  <option value="project">Project</option>
                  <option value="essay">Essay</option>
                </select>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="form-select"
                >
                  <option value="">Subject (optional)</option>
                  {enabledClasses.map(cls => (
                    <option key={cls.period} value={cls.subject}>{cls.subject}</option>
                  ))}
                </select>
              </div>
              <div className="form-row form-row--split">
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Notes (optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn--secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary" disabled={!title.trim() || !dueDate}>
                  {editingId ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          )}

          {assignments.length === 0 && !showAddForm && (
            <p className="long-term-empty">No upcoming assignments or tests</p>
          )}

          {/* Incomplete assignments */}
          {incompleteAssignments.length > 0 && (
            <div className="long-term-list">
              {incompleteAssignments.map(assignment => (
                <div
                  key={assignment.id}
                  className={`long-term-item ${getDueDateClass(assignment.due_date)}`}
                >
                  <div className="long-term-item-check">
                    <input
                      type="checkbox"
                      checked={assignment.completed}
                      onChange={(e) => onUpdate(assignment.id, { ...assignment, completed: e.target.checked })}
                    />
                  </div>
                  <div className="long-term-item-content">
                    <div className="long-term-item-header">
                      <span className={`long-term-type long-term-type--${assignment.type}`}>
                        {assignment.type === 'test' ? '📝' : '📚'}
                      </span>
                      <span className="long-term-item-title">{assignment.title}</span>
                      {assignment.subject && (
                        <span className="long-term-subject">{assignment.subject}</span>
                      )}
                    </div>
                    {assignment.description && (
                      <p className="long-term-description">{assignment.description}</p>
                    )}
                  </div>
                  <div className="long-term-item-meta">
                    <span className={`long-term-due ${getDueDateClass(assignment.due_date)}`}>
                      {getDueDateLabel(assignment.due_date)}
                    </span>
                    <div className="long-term-actions">
                      <button
                        className="long-term-action-btn"
                        onClick={() => startEdit(assignment)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="long-term-action-btn"
                        onClick={() => handleDelete(assignment)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Divider between incomplete and completed */}
          {completedAssignments.length > 0 && (
            <>
              <div className="long-term-divider">
                <span>Completed</span>
              </div>
              <div className="long-term-list">
                {completedAssignments.map(assignment => (
                  <div
                    key={assignment.id}
                    className="long-term-item completed"
                  >
                    <div className="long-term-item-check">
                      <input
                        type="checkbox"
                        checked={assignment.completed}
                        onChange={(e) => onUpdate(assignment.id, { ...assignment, completed: e.target.checked })}
                      />
                    </div>
                    <div className="long-term-item-content">
                      <div className="long-term-item-header">
                        <span className={`long-term-type long-term-type--${assignment.type}`}>
                          {assignment.type === 'test' ? '📝' : '📚'}
                        </span>
                        <span className="long-term-item-title">{assignment.title}</span>
                        {assignment.subject && (
                          <span className="long-term-subject">{assignment.subject}</span>
                        )}
                      </div>
                      {assignment.description && (
                        <p className="long-term-description">{assignment.description}</p>
                      )}
                    </div>
                    <div className="long-term-item-meta">
                      <span className="long-term-due">
                        {getDueDateLabel(assignment.due_date)}
                      </span>
                      <div className="long-term-actions">
                        <button
                          className="long-term-action-btn"
                          onClick={() => startEdit(assignment)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="long-term-action-btn"
                          onClick={() => handleDelete(assignment)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default LongTermSection;
